import React from 'react';

/** Props for ChartLoadingSkeleton component */
interface ChartLoadingSkeletonProps {
  /** Optional CSS class name for the container */
  className?: string;
}

const SKELETON_BARS = [
  { bar: 75, line: 82 },
  { bar: 45, line: 52 },
  { bar: 85, line: 88 },
  { bar: 60, line: 68 },
  { bar: 90, line: 92 },
  { bar: 50, line: 58 },
  { bar: 78, line: 85 },
  { bar: 42, line: 50 },
  { bar: 88, line: 90 },
  { bar: 55, line: 62 },
  { bar: 70, line: 78 },
  { bar: 48, line: 55 },
];

const TIME_FRAME_OPTIONS = [
  'Past 7 Days',
  'Past 30 Days',
  'Past 60 Days',
  'This Quarter',
  'This Year',
  'Past Year',
  'Custom',
];

const GROUP_BY_OPTIONS = ['Day', 'Week', 'Month', 'Quarter', 'Year', 'All'];

/**
 * Loading skeleton for the Market Performance Chart.
 * Displays a realistic preview of the chart structure during data fetch.
 */
export const ChartLoadingSkeleton: React.FC<ChartLoadingSkeletonProps> = ({ className }) => (
  <div className={className}>
    <div className="flex items-center justify-between mb-4">
      <h2 className="text-lg font-semibold text-gray-900">Overall Market Performance</h2>
    </div>
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
      <div className="flex">
        <div className="flex-1 animate-pulse">
          <div className="flex h-[440px]">
            {/* Left Y-axis labels */}
            <div className="w-14 flex flex-col justify-between py-6 pr-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-3 bg-gray-200 rounded w-10" />
              ))}
            </div>

            {/* Chart area */}
            <div className="flex-1 flex flex-col relative">
              {/* Grid lines */}
              <div className="absolute inset-0 flex flex-col justify-between py-6 pointer-events-none">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="border-b border-gray-100 w-full" />
                ))}
              </div>

              {/* Chart bars and line markers */}
              <div className="flex-1 flex items-end justify-around gap-1 px-2 pb-8 relative z-10">
                {SKELETON_BARS.map((data, i) => (
                  <div key={i} className="flex flex-col items-center flex-1 h-full relative">
                    <div
                      className="absolute w-3 h-3 rounded-full bg-lime-300 border-2 border-white shadow-sm z-20"
                      style={{ bottom: `calc(${data.line}% + 32px)` }}
                    />
                    <div className="absolute bottom-0 w-full flex justify-center">
                      <div className="w-8 bg-green-300 rounded-t-md" style={{ height: `${data.bar * 3.2}px` }} />
                    </div>
                  </div>
                ))}
              </div>

              {/* Gradient fill */}
              <div className="absolute inset-x-0 bottom-8 h-[60%] bg-gradient-to-t from-lime-100/50 to-transparent pointer-events-none" />

              {/* X-axis labels */}
              <div className="flex justify-around pt-3 px-2">
                {[1, 2, 3, 4, 5, 6, 7].map((i) => (
                  <div key={i} className="h-3 bg-gray-200 rounded w-10" />
                ))}
              </div>
            </div>

            {/* Right Y-axis (Yield %) */}
            <div className="w-10 flex flex-col justify-between py-6 pl-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-3 bg-gray-200 rounded w-6 ml-auto" />
              ))}
            </div>

            {/* Far right Y-axis (Divisions) */}
            <div className="w-8 flex flex-col justify-between py-6 pl-1">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-3 bg-gray-200 rounded w-4 ml-auto" />
              ))}
            </div>
          </div>

          {/* Legend */}
          <div className="flex justify-center gap-8 pt-4">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full border-2 border-lime-400 bg-white" />
              <span className="text-sm text-gray-400">Yield</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-green-400" />
              <span className="text-sm text-gray-400">Revenue</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full border-2 border-lightBlue-400 bg-white" />
              <span className="text-sm text-gray-400">Divisions Awarded</span>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="w-36 pl-4 flex-shrink-0 animate-pulse">
          <div className="h-5 bg-gray-300 rounded w-24 mb-3" />
          <div className="space-y-2 mb-6">
            {TIME_FRAME_OPTIONS.map((_, i) => (
              <div key={i} className={`h-4 rounded w-20 ${i === 0 ? 'bg-green-200' : 'bg-gray-200'}`} />
            ))}
          </div>
          <div className="h-5 bg-gray-300 rounded w-20 mb-3" />
          <div className="space-y-2">
            {GROUP_BY_OPTIONS.map((_, i) => (
              <div key={i} className={`h-4 rounded w-16 ${i === 0 ? 'bg-green-200' : 'bg-gray-200'}`} />
            ))}
          </div>
        </div>
      </div>
    </div>
  </div>
);
